/**
 * File: paymentRemindersUtils.ts
 * Path: apps/admin/src/shared/utils/paymentRemindersUtils.ts
 * Description: The history of WhatsApp reminders sent for late payments, so
 * the Money owed page can say "Last reminded 3 Oct" and the 07:00 push can
 * count the late payments nobody has chased lately. One create-only document
 * per reminder under contracts/{id}/paymentReminders; never edited or deleted.
 */

import { isDueDate } from "@idk/config";

/** A late payment not reminded for this many days is chased again. */
export const PAYMENT_REMINDER_GAP_DAYS = 7;

const DAY_MS = 86_400_000;

export interface PaymentReminder {
  id: string;
  /** The payment's reference, e.g. "IDK-C012-02-DEP". */
  reference: string;
  /** YYYY-MM-DD (South Africa) the reminder was sent. */
  sentOn: string;
}

export const toPaymentReminder = (
  id: string,
  data: Record<string, unknown> | null | undefined,
): PaymentReminder | null => {
  const reference = typeof data?.reference === "string" ? data.reference : "";
  const sentOn = typeof data?.sentOn === "string" ? data.sentOn : "";
  return reference && isDueDate(sentOn) ? { id, reference, sentOn } : null;
};

/** The most recent reminder day for each payment reference. */
export const lastRemindedByReference = (
  reminders: readonly PaymentReminder[],
): Map<string, string> => {
  const latest = new Map<string, string>();
  for (const { reference, sentOn } of reminders) {
    const seen = latest.get(reference);
    if (!seen || sentOn > seen) latest.set(reference, sentOn);
  }
  return latest;
};

/** True when no reminder went out in the last PAYMENT_REMINDER_GAP_DAYS days. */
export const needsReminder = (lastRemindedOn: string | undefined, today: string): boolean =>
  !lastRemindedOn ||
  (Date.parse(`${today}T00:00:00Z`) - Date.parse(`${lastRemindedOn}T00:00:00Z`)) / DAY_MS >=
    PAYMENT_REMINDER_GAP_DAYS;
